import { useState } from "react";

// Horizontally swipable photo carousel using native CSS scroll-snap. No deps.
// On desktop the user can drag with the mouse and click dots to jump; on
// mobile they swipe. Browser handles the physics.

interface Props {
  urls: string[];
}

export function PhotoCarousel({ urls }: Props) {
  const [active, setActive] = useState(0);

  if (urls.length === 0) return null;

  return (
    <section aria-label="Photos" className="w-full">
      <div
        className="no-scrollbar flex overflow-x-auto snap-x snap-mandatory scroll-smooth"
        onScroll={(e) => {
          // Update the active dot indicator based on which photo is closest
          // to the leading edge.
          const el = e.currentTarget;
          const idx = Math.round(el.scrollLeft / el.clientWidth);
          if (idx !== active && idx >= 0 && idx < urls.length) setActive(idx);
        }}
      >
        {urls.map((url, i) => (
          <div
            key={url + i}
            className="snap-start shrink-0 w-full aspect-square bg-surfaceAlt flex items-center justify-center"
          >
            <img
              src={url}
              alt=""
              loading={i === 0 ? "eager" : "lazy"}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Replace broken image with a subtle placeholder rather than
                // letting the browser draw its grey box.
                const img = e.currentTarget;
                img.style.display = "none";
                img.parentElement?.classList.add("photo-broken");
              }}
            />
          </div>
        ))}
      </div>

      {urls.length > 1 && (
        <div className="flex justify-center gap-1.5 py-3">
          {urls.map((_, i) => (
            <span
              key={i}
              className={
                "transition-all rounded-full " +
                (i === active
                  ? "w-6 h-1.5 bg-primary"
                  : "w-1.5 h-1.5 bg-border")
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}
